import { useMemo } from "react";

import {
  useGetAuthenticatedUserUserGetQuery,
  useListZonesZonesGetQuery,
} from "api";
import { Zone } from "api/generatedApi";
import { IZone } from "interfaces/zone.interface";

function hasZoneAccess(
  zone: Zone | IZone,
  authZones: Zone[],
): { hasAccess: boolean; hasPartialAccess: boolean } {
  const hasAccess = zone.path
    .split(".")
    .some((zoneId) => authZones.some((zone) => zone.id === zoneId));

  const hasPartialAccess =
    hasAccess ||
    authZones.flatMap((zone) => zone.path.split(".")).includes(zone.id);

  return { hasAccess, hasPartialAccess };
}

export function computeZones(
  allZones?: (Zone | IZone)[] | null,
  authZones?: Zone[] | null,
): IZone[] {
  return (
    allZones
      ?.map((zone) => ({
        ...zone,
        parent_id: zone.parent_id ?? null,
        level: zone.level ?? 0,
        ...hasZoneAccess(zone, authZones ?? []),
      }))
      ?.filter(({ hasPartialAccess }) => hasPartialAccess) ?? []
  );
}

type Return = {
  zones: IZone[];
  isLoading: boolean;
  isError: boolean;
};

export const useGetZones = (): Return => {
  const {
    data: zonesData,
    isLoading: isLoadingZones,
    isError: isErrorZones,
  } = useListZonesZonesGetQuery();

  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useGetAuthenticatedUserUserGetQuery();

  const isLoading = isLoadingZones || isLoadingUser;
  const isError = isErrorZones || isErrorUser;

  return useMemo(() => {
    return {
      isError,
      isLoading,
      zones: computeZones(zonesData?.data, userData?.data?.zones?.data),
    };
  }, [isError, isLoading, userData, zonesData]);
};
